import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Panel } from '../../components/Panel';
import { Button } from '../../components/Button';
import { Spinner, ErrorBox } from '../../components/Spinner';
import { useApi } from '../../hooks/useApi';
import { getMe, registerUser } from '../../api/endpoints';
import type { User } from '../../types/api';
import { ROLE_LABEL, ROLE_COLOR, isActiveRole } from './roles';

// 初回登録・承認待ち・無効ユーザーの受け皿（Layout から未利用ユーザーがリダイレクトされる）
export function RegisterPage() {
  const me = useApi(getMe, []);

  if (me.loading) return <Spinner />;
  if (me.error) return <ErrorBox error={me.error} />;
  if (!me.data) return null;

  let content;
  if (!me.data.registered) {
    // 未登録 → 初回登録
    // 登録成功時はハードリロードで全体の getMe を最新化する。
    // Layout も独自に getMe を保持しており、refetch だけだと未登録状態が残って
    // /register にリダイレクトし続けるループになるため（手動リロード回避）。
    content = (
      <RegisterCard
        accountLabel={me.data.email || me.data.id || ''}
        onRegistered={() => window.location.assign('/')}
      />
    );
  } else if (isActiveRole(me.data.user!.role) && me.data.user!.enabled) {
    // 利用可能ロール（通常 Layout 側で本体へ通すのでここには来ない）
    content = (
      <Panel title="アカウント情報">
        <p className="text-sm">利用可能です。</p>
        <Link to="/" className="text-primary text-sm underline">
          トップへ
        </Link>
      </Panel>
    );
  } else {
    // 承認待ち / 無効
    content = <SelfStatusCard user={me.data.user!} />;
  }

  return (
    <div>
      <div className="flex justify-center mb-6 mt-4">
        <img src="/logo-horizontal.png" alt="三興バルブ HOLDINGS" className="h-16" />
      </div>
      {content}
    </div>
  );
}

// ── 初回登録（担当者コード照合 → pending） ──
function RegisterCard({
  accountLabel,
  onRegistered,
}: {
  accountLabel: string;
  onRegistered: () => void;
}) {
  const [displayName, setDisplayName] = useState('');
  const [staffCode, setStaffCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function submit() {
    setBusy(true);
    setError('');
    try {
      await registerUser({ display_name: displayName, staff_code: staffCode });
      onRegistered();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel title="ユーザー登録（初回）">
      <p className="text-sm text-muted mb-3">
        担当者マスタの担当者コードを入力してください。承認待ち（pending）で登録され、管理者の承認後に利用できます。
      </p>
      <div className="space-y-2 max-w-md">
        <label className="block text-sm">
          アカウント
          <input
            type="text"
            className="border rounded px-3 py-1.5 text-sm w-full bg-slate-50 font-mono"
            value={accountLabel}
            readOnly
          />
        </label>
        <label className="block text-sm">
          表示名
          <input
            type="text"
            className="border rounded px-3 py-1.5 text-sm w-full"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="山田 太郎"
          />
        </label>
        <label className="block text-sm">
          担当者コード
          <input
            type="text"
            className="border rounded px-3 py-1.5 text-sm w-full font-mono"
            value={staffCode}
            onChange={(e) => setStaffCode(e.target.value)}
            placeholder="0318"
          />
        </label>
        {error && <div className="bg-red-50 text-red-800 px-3 py-2 rounded text-sm">{error}</div>}
        <Button onClick={submit} disabled={busy || !staffCode}>
          {busy ? '登録中...' : '登録する'}
        </Button>
      </div>
    </Panel>
  );
}

// ── 承認待ち / 無効の自分ステータス ──
function SelfStatusCard({ user }: { user: User }) {
  return (
    <Panel title="アカウント情報">
      <div className="text-sm space-y-1">
        {user.email && (
          <div>
            メール: <span className="font-mono">{user.email}</span>
          </div>
        )}
        <div>表示名: {user.display_name}</div>
        <div>
          担当者: <span className="font-mono">{user.staff_code} {user.staff_name}</span>
        </div>
        <div>
          ロール:{' '}
          <span className={`inline-block px-2 py-0.5 rounded text-xs ${ROLE_COLOR[user.role]}`}>
            {ROLE_LABEL[user.role]}
          </span>
        </div>
      </div>
      {user.role === 'pending' && (
        <div className="bg-amber-50 text-amber-800 px-3 py-2 rounded text-sm mt-3">
          現在は承認待ちです。管理者がロールを設定すると利用できるようになります。
        </div>
      )}
      {!user.enabled && (
        <div className="bg-slate-100 text-slate-700 px-3 py-2 rounded text-sm mt-3">
          このアカウントは無効化されています。管理者にお問い合わせください。
        </div>
      )}
    </Panel>
  );
}
